import { HeatmapLayer } from "@deck.gl/aggregation-layers/typed";
import { MAP_THEME } from "./ChainMap.theme";
import { HeatmapPoint } from "../../../../../core/models/Heatmap";

export const MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '';

export const DECK_HEATMAP_CANVAS_ATTR = "data-deck-heatmap";

export const MAP_LOADER_ID = "GOOGLE_MAP_LOADER"
export enum MAP_IDS {
    PROFILE = "GOOGLE_MAP_PROFILE",
    CAROUSEL = "GOOGLE_MAP_CAROUSEL"
}

export const MAP_LOADER_OPTIONS = {
    id: MAP_LOADER_ID,
    googleMapsApiKey: MAP_API_KEY,
    language: "en",
    mapIds: [MAP_IDS.PROFILE, MAP_IDS.CAROUSEL],
}

export const MAP_CENTER = {
    lat: 37.7,
    lng: -97.21,
};

export const defaultContainerStyle = {
    width: "100%",
    height: "100%",
    minHeight: 200,
};
export const MAP_DEFAULT_ZOOM = 4;
export interface iMapConfig extends google.maps.MapOptions {
    minZoom: number;
    maxZoom: number;
}
export const MAP_OPTIONS: iMapConfig = {
    zoom: MAP_DEFAULT_ZOOM,
    minZoom: 2,
    maxZoom: 12,
    disableDefaultUI: true,
    styles: MAP_THEME,
    disableDoubleClickZoom: true,
    gestureHandling: "cooperative",
};

export const HEATMAP_COLOR_RANGE: [number, number, number, number][] = [
    [76, 132, 174, 0],
    [120, 195, 45, 70],
    [130, 205, 20, 170],
    [134, 208, 4, 230],
    [134, 208, 4, 255],
    [134, 208, 4, 255],
];

export const HEATMAP_OPACITY = 0.7;
export const HEATMAP_INTENSITY = 90;
export const HEATMAP_RADIUS_SCALE = 1.3;
export const HEATMAP_REFERENCE_WIDTH = 1900;
export const HEATMAP_MAX_RADIUS_BOOST = 1.7;

function getSizeBoost(containerWidth: number) {
    const width = containerWidth || HEATMAP_REFERENCE_WIDTH;
    return Math.min(Math.max(HEATMAP_REFERENCE_WIDTH / width, 1), HEATMAP_MAX_RADIUS_BOOST);
}

export function normZoom(zoom: number) {
    const { maxZoom, minZoom } = MAP_OPTIONS;
    return (zoom - minZoom) / (maxZoom - minZoom);
}

export function getCurve(x: number) {
    const scale = 2;
    return -scale * (x - 1) ** 2 + scale + 0.7;
}

export function getRadius(zoom: number) {
    const normalizedZoom = normZoom(zoom);
    return zoom * getCurve(normalizedZoom);
}

export const HEATMAP_BASE_RADIUS = getRadius(MAP_DEFAULT_ZOOM);

export function createHeatmapLayer(data: HeatmapPoint[], radiusPixels: number, containerWidth: number) {
    return new HeatmapLayer<HeatmapPoint>({
        id: "chain-profile-heatmap",
        data,
        getPosition: (d) => d,
        radiusPixels: radiusPixels * HEATMAP_RADIUS_SCALE * getSizeBoost(containerWidth),
        intensity: HEATMAP_INTENSITY,
        aggregation: "SUM",
        colorRange: HEATMAP_COLOR_RANGE,
        opacity: HEATMAP_OPACITY,
        debounceTimeout: 0,
    });
}

export const getDeckHeatmapCanvas = (mapNode: HTMLElement): HTMLCanvasElement | null => {
    return mapNode.querySelector<HTMLCanvasElement>(`canvas[${DECK_HEATMAP_CANVAS_ATTR}]`);
};

export const trimCanvas = (canvas: HTMLCanvasElement) => {
    const rowBlank = (imageData: any, width: number, y: number) => {
        for (var x = 0; x < width; ++x) {
            if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
        }
        return true;
    }

    const columnBlank = (imageData: any, width: number, x: number, top: number, bottom: number) => {
        for (var y = top; y < bottom; ++y) {
            if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
        }
        return true;
    }
    const copiedCanv = () => {
        var ctx = canvas.getContext("2d");
        var width = canvas.width;
        if ( ctx ) {
            var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            var top = 0, bottom = imageData.height, left = 0, right = imageData.width;

            while (top < bottom && rowBlank(imageData, width, top)) ++top;
            while (bottom - 1 > top && rowBlank(imageData, width, bottom - 1)) --bottom;
            while (left < right && columnBlank(imageData, width, left, top, bottom)) ++left;
            while (right - 1 > left && columnBlank(imageData, width, right - 1, top, bottom)) --right;

            var trimmed = ctx.getImageData(left, top, right - left, bottom - top);
            var copy = canvas.ownerDocument.createElement("canvas");
            var copyCtx = copy.getContext("2d");
            copy.width = trimmed.width;
            copy.height = trimmed.height;
            if ( copyCtx ) {
                copyCtx.putImageData(trimmed, 0, 0);
                return copy;
            }

        }
        return canvas;
    }
    return copiedCanv();
}

export const copyImage = (canvas: HTMLCanvasElement) => {
    navigator.clipboard.writeText(canvas.toDataURL());
}

export const appendImage = (data: string) => {
    let image = new Image();
    image.crossOrigin = "anonymous";
    image.src = data;
    const root = document.querySelector(".ChainPage__description");

    if ( root ) {
        root.appendChild(image);
    }
}
interface iCanvasData {
    left: number;
    top: number;
    size: number;
    el: HTMLCanvasElement | HTMLImageElement;
};
export const postChainMapDataURL = (
    querySelector: string,
    callback: (imageData: string) => void,
    debug: boolean = false,
) => {
    const outputDimensions = { width: 620, height: 370 };
    const mapNode = document.querySelector(querySelector) as HTMLElement;
    const mapTiles = mapNode.querySelectorAll("img");
    let loadedImagesCount = 0;
    const imageTilesData: iCanvasData[] = [];

    const getNewCanvasSize = (data: iCanvasData[]) => {
        const minLeft = Math.min.apply(null, data.map(item => item.left));
        const maxLeft = Math.max.apply(null, data.map(item => item.left));

        const minTop = Math.min.apply(null, data.map(item => item.top));
        const maxTop = Math.max.apply(null, data.map(item => item.top));

        const size = Math.max.apply(null, data.map(item => item.size))


        return {
            width: Math.abs(minLeft) + maxLeft + size,
            height: Math.abs(minTop) + maxTop + size,
            shift: {
                left: Math.abs(minLeft),
                top: Math.abs(minTop)
            }
        }
    }

    const getMergedCanvas = (data: iCanvasData[]) => {
        const { width, height, shift } = getNewCanvasSize(data);

        let newCanvas = document.createElement('canvas');
        let context = newCanvas.getContext('2d');

        newCanvas.width = width;
        newCanvas.height = height;

        data.forEach((canvas) => {
            if ( context ) {
                context.beginPath();
                context.drawImage(
                    canvas.el,
                    canvas.left + shift.left,
                    canvas.top + shift.top,
                    canvas.size,
                    canvas.size
                );
            }
        });

        return newCanvas;
    };

    const getHeatmapCanvas = (size: ReturnType<typeof getNewCanvasSize>): HTMLCanvasElement | null => {
        const deckCanvas = getDeckHeatmapCanvas(mapNode);
        if ( !deckCanvas ) return null;

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = size.width;
        canvas.height = size.height;

        if ( context ) {
            context.drawImage(
                deckCanvas,
                size.shift.left,
                size.shift.top,
                mapNode.clientWidth,
                mapNode.clientHeight,
            );
        }

        return canvas;
    };

    const getOutputCanvas = (canvases: HTMLCanvasElement[]) => {
        let canvas = document.createElement("canvas");
        let context = canvas.getContext("2d") as CanvasRenderingContext2D;
        canvas.width = outputDimensions.width;
        canvas.height = outputDimensions.height;
        const scale = 0.85;
        const x = -65;
        const y = -70;

        canvases.forEach(el => {
            context.drawImage(
                el,
                x,
                y,
                el.width * scale,
                el.height * scale,
            );
        });

        return canvas;
    }

    const imageLoaded = () => {
        loadedImagesCount++;

        if (loadedImagesCount === mapTiles.length) {
            const backgroundSize = getNewCanvasSize(imageTilesData);
            const background = getMergedCanvas(imageTilesData);
            const heatmap = getHeatmapCanvas(backgroundSize);
            const layers = [background, heatmap].filter(Boolean) as HTMLCanvasElement[];
            const canvas = getOutputCanvas(layers);
            const base64Canvas = canvas.toDataURL();

            if ( debug ) {
                const newTab = window.open() as any;
                newTab.document.body.innerHTML = `<img src="${base64Canvas}" />`;
            }

            callback(base64Canvas);
        }
    }

    const generateCanvas = () => {
        Array.from(mapTiles).forEach(image => {
            setTimeout(() => {
                const parentNode = image.parentElement as HTMLElement;
                const imgEl = document.createElement('img');
                imgEl.onload = imageLoaded;
                imgEl.crossOrigin = "anonymous";
                imgEl.src = image.src;
                imageTilesData.push({
                    el: imgEl,
                    left: parseInt(parentNode.style.left),
                    top: parseInt(parentNode.style.top),
                    size: parseInt(parentNode.style.width)
                });
            });
        });
    }

    generateCanvas();
};
